import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Props:
 * - title?: string — used when no tabs are supplied
 * - tabs?: [{ id: string, label: string }]
 * - activeTab?: string
 * - onTabChange?: (id: string) => void
 * - children: ReactNode
 * - defaultCollapsed?: boolean
 * - forceExpandSignal?: any
 */
const CollapsibleSection = ({
  title,
  tabs,
  activeTab,
  onTabChange,
  children,
  defaultCollapsed = false,
  forceExpandSignal,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // measure the floating header (tabs/title) to reserve space above the card
  const headerRef = useRef(null);
  const [headerH, setHeaderH] = useState(0);

  const measure = () => {
    const h = headerRef.current?.getBoundingClientRect?.().height || 0;
    // Add a tiny breathing room (8px) so it doesn’t visually kiss the border
    setHeaderH(Math.ceil(h + 8));
  };

  useLayoutEffect(() => {
    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // re-measure when tab set or active tab changes
  }, []);

  useEffect(() => {
    measure();
  }, [tabs?.length, activeTab, title]);

  useEffect(() => {
    if (forceExpandSignal) setCollapsed(false);
  }, [forceExpandSignal]);

  const clickTab = (id) => {
    if (!tabs || tabs.length === 0) return;
    if (id === activeTab) {
      // clicking the active tab toggles collapse
      setCollapsed((c) => !c);
    } else {
      // switching tabs always expands
      onTabChange?.(id);
      setCollapsed(false);
    }
  };

  return (
    <div
      className="relative bg-white rounded-2xl border border-gray-200 shadow-sm pt-4 mb-4"
      // Reserve exact space above for the floating header
      style={{ marginTop: headerH || 0 }}
    >
      {/* Floating header ABOVE the card; bottom edges align with the top border */}
      <div
        ref={headerRef}
        className="absolute left-4 z-10 flex items-end gap-2"
        style={{ top: 0, transform: "translateY(-100%)" }}
      >
        {tabs && tabs.length > 0 ? (
          <div className="flex">
            {tabs.map((tab, idx) => {
              const isActive = activeTab === tab.id;
              const radius =
                idx === 0
                  ? "rounded-tl-md rounded-tr-none"
                  : idx === tabs.length - 1
                  ? "rounded-tr-md rounded-tl-none"
                  : "rounded-none";
              return (
                <button
                  key={tab.id}
                  onClick={() => clickTab(tab.id)}
                  className={[
                    // slimmer, light tabs
                    "px-3 py-0 text-sm font-semibold border shadow-sm transition",
                    isActive
                      ? "bg-indigo-100 text-indigo-700 border-indigo-300"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
                    radius,
                  ].join(" ")}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        ) : (
          // Title pill (no tabs): clicking toggles collapse
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="px-3 py-0 text-sm font-semibold bg-indigo-100 text-indigo-700 border border-indigo-300 rounded-t-md shadow-sm hover:bg-indigo-50 transition"
            title={collapsed ? "Expand section" : "Collapse section"}
          >
            {title}
          </button>
        )}
      </div>

      {!collapsed && <div className="p-4 pt-2">{children}</div>}
    </div>
  );
};

export default CollapsibleSection;
